/**
 * Migration 0095 — account-level request-and-approve settings, mounted on
 * /settings/account below the core account form.
 *
 *   - Require approval for a new customer's first booking (toggle).
 *   - Booking request expiry window in hours (1..720).
 *
 * Self-contained: loads + saves via `@/lib/booking_requests_api` (raw fetch
 * against /api/me/booking-request-settings) so the AccountSettings test
 * mocks of `@/lib/api` are unaffected. Renders a compact skeleton until the
 * settings load and fails soft when they can't.
 */
import {
  getBookingRequestSettings,
  updateBookingRequestSettings,
} from '@/lib/booking_requests_api';
import type { BookingRequestSettings } from '@/lib/types';
import { useEffect, useState } from 'react';

export function BookingRequestSettingsSection() {
  const [loaded, setLoaded] = useState<BookingRequestSettings | null>(null);
  const [requireApproval, setRequireApproval] = useState(false);
  const [expiryHours, setExpiryHours] = useState('48');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getBookingRequestSettings();
        if (cancelled) return;
        setLoaded(res.settings);
        setRequireApproval(res.settings.requireApprovalForNewCustomers);
        setExpiryHours(String(res.settings.bookingRequestExpiryHours));
      } catch {
        if (!cancelled) setUnavailable(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const parsedHours = Number.parseInt(expiryHours, 10);
  const hoursValid = Number.isInteger(parsedHours) && parsedHours >= 1 && parsedHours <= 720;
  const dirty =
    loaded !== null &&
    (requireApproval !== loaded.requireApprovalForNewCustomers ||
      parsedHours !== loaded.bookingRequestExpiryHours);

  async function handleSave() {
    if (!loaded || !hoursValid) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await updateBookingRequestSettings({
        ...(requireApproval !== loaded.requireApprovalForNewCustomers
          ? { requireApprovalForNewCustomers: requireApproval }
          : {}),
        ...(parsedHours !== loaded.bookingRequestExpiryHours
          ? { bookingRequestExpiryHours: parsedHours }
          : {}),
      });
      setLoaded(res.settings);
      setMessage('Booking request settings saved.');
    } catch {
      setError('Could not save booking request settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (unavailable) return null;

  return (
    <section
      className="mt-8 rounded-xl border border-border bg-surface"
      data-testid="booking-request-settings"
      aria-labelledby="booking-request-settings-heading"
    >
      <div className="border-b border-border px-6 py-5">
        <h2 id="booking-request-settings-heading" className="text-base font-medium">
          Booking requests
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose per service whether online bookings confirm instantly or wait for your approval
          (Settings → Services). These account-wide rules apply on top.
        </p>
      </div>

      {loaded === null ? (
        <div className="space-y-3 px-6 py-5">
          <div className="h-4 w-2/3 animate-pulse rounded bg-secondary" />
          <div className="h-4 w-1/3 animate-pulse rounded bg-secondary" />
        </div>
      ) : (
        <>
          <div className="divide-y divide-border">
            <div className="px-6 py-5">
              <label className="flex cursor-pointer items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={requireApproval}
                  onChange={(e) => setRequireApproval(e.target.checked)}
                  className="mt-0.5 rounded border-border"
                />
                <span>
                  <span className="font-medium">
                    Always review a new customer&rsquo;s first booking
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Their first booking lands in your Requests queue even for instant-confirmation
                    services. Later bookings follow the service setting.
                  </span>
                </span>
              </label>
            </div>
            <div className="px-6 py-5">
              <label
                htmlFor="booking-request-expiry-hours"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Auto-decline unanswered requests after (hours)
              </label>
              <input
                id="booking-request-expiry-hours"
                type="number"
                min={1}
                max={720}
                step={1}
                value={expiryHours}
                onChange={(e) => setExpiryHours(e.target.value)}
                className="num w-32 rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Requests hold capacity while you decide. After this window the request is declined
                automatically and the customer is told. 1&ndash;720 hours.
              </p>
              {!hoursValid && (
                <p className="mt-1 text-xs text-destructive">
                  Enter a whole number between 1 and 720.
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-border px-6 py-5">
            {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
            {message && <p className="mb-3 text-sm text-success">{message}</p>}
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!dirty || !hoursValid || saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save booking request settings'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
